import type { ReactNode } from "react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { useAuth } from "../hooks/useAuth";
import AppShell from "./AppShell";
import { GuestTopBar } from "../components/GuestTopBar";

// Tool pages (map beside results) run edge to edge signed out too, like the
// signed-in app; the 960 px reading column squashes them.
const WIDE_PREFIXES = ["/route-analyser", "/routes/"];

/**
 * Layout for routes that must stay reachable while signed out — the
 * programmatic-SEO surface (collections, regions, trails) — but should still
 * get the full authenticated AppShell chrome for signed-in visitors.
 *
 * Unlike `_protected` (see router.tsx), this route has no `beforeLoad` auth
 * redirect: it only pre-warms the `["me"]` query cache so `useAuth()` below
 * resolves without a loading flash. An anonymous visitor gets a slim public
 * header (logo + log in / sign up) instead of the full sidebar, which
 * assumes a logged-in user (wallet balance, profile card, admin nav, etc).
 */
export default function PublicShell({ children }: { children?: ReactNode } = {}) {
  const { user } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const wide = WIDE_PREFIXES.some((p) => pathname.startsWith(p));

  if (user) {
    return <AppShell>{children ?? <Outlet />}</AppShell>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-app)", display: "flex", flexDirection: "column" }}>
      <GuestTopBar />
      <div className={wide ? "atlas-guest-column atlas-guest-column--wide" : "atlas-guest-column"}>{children ?? <Outlet />}</div>
    </div>
  );
}
